import { useMemo, useRef } from "react";

import type { SearchableSelectionOption } from "app/pages/sales-intelligence/common-components/column-picker";

import { createColumnSearchIndex } from "../services/search/createColumnSearchIndex";
import type { IColumnSearchIndex } from "../services/search/types";
import { filterAndSortByScores, partitionOptionsBySelectedIds } from "../utils/columns";

import { useAppliedSearchTerm } from "./useAppliedSearchTerm";

type HookParams<O> = {
    columnOptions: readonly O[];
    selectedIds: readonly string[];
    selectedCategory: string;
    searchTerm: string;
    locale: string;
    isAnyCatagegorySelected: boolean;
};

type HookResult<O> = {
    selectedOptions: readonly O[];
    notSelectedOptions: readonly O[];
    /** The term the results actually reflect — highlight with this one, not the raw input. */
    appliedSearchTerm: string;
};

export const useColumnOptions = <O extends SearchableSelectionOption>({
    columnOptions,
    selectedIds,
    selectedCategory,
    searchTerm,
    locale,
    isAnyCatagegorySelected,
}: HookParams<O>): HookResult<O> => {
    const appliedSearchTerm = useAppliedSearchTerm(searchTerm);

    /*
     * One index per picker instance. Module scope would be wrong — the engine holds a single-entry
     * cache and two pickers can be mounted at once, so they would evict each other on every
     * alternating query. useMemo would be wrong too, since React may discard its result.
     */
    const indexRef = useRef<IColumnSearchIndex | null>(null);
    indexRef.current ??= createColumnSearchIndex();
    const index = indexRef.current;

    const [selectedOptions, notSelectedOptions] = useMemo(
        () => partitionOptionsBySelectedIds(columnOptions, selectedIds),
        [columnOptions, selectedIds],
    );

    /*
     * Matched against the full option list rather than the non-selected slice: the engine caches its
     * normalised documents on the source reference, and that slice gets a new identity every time a
     * column is added or removed.
     */
    const scoresById = useMemo(
        () =>
            appliedSearchTerm.trim() ? index.match({ source: columnOptions, query: appliedSearchTerm, locale }) : null,
        [index, columnOptions, appliedSearchTerm, locale],
    );

    const filteredNotSelectedOptions = useMemo(
        () => filterAndSortByScores(notSelectedOptions, scoresById, selectedCategory, isAnyCatagegorySelected),
        [notSelectedOptions, scoresById, selectedCategory, isAnyCatagegorySelected],
    );

    return { selectedOptions, notSelectedOptions: filteredNotSelectedOptions, appliedSearchTerm };
};
